import React from 'react';
import bemBlock from '@gik/core/utils/bemBlock';
import {
  Button,
  Form,
  FormButtons,
  FormField,
  FormProps,
  HTMLParser,
  PageSection,
  SvgIcon,
  validateForm,
  ValidationErrors,
  ValidationOptions,
} from '@gik/ui';
import { FormRenderProps } from 'react-final-form';
import { pageNameStepSchema } from '@gik/create/formSchemas';
import { useTranslation } from 'react-i18next';
import { translationKeys } from '@gik/create/i18n/en';
import { CreatePageContext } from '@gik/create/components/CreatePage/CreatePage';
import { useCreateFlowStore } from '@gik/create';
import RecipientInfoAddressForm, {
  IRecipientInfoAddressFormValues,
  recipientInfoAddressFormSchema,
} from '@gik/create/components/RecipientInfoStep/RecipientInfoAddressForm';
import { RecipientType } from '@gik/create/enums/RecipientType';
import { CreateAnalyticsEvents } from '@gik/create/hooks/useCreateAnalytics';
import { PageNameStepInputNames } from '@gik/create/enums/PageNameStepInputNames';
import { AboutStepInputNames } from '@gik/create/enums/AboutStepInputNames';

export interface INameYourPageStepProps extends FormProps {
  buttons?: (form: FormRenderProps, formId: string) => React.ReactNode;
  buttonsPortal?: () => HTMLElement;
  onSubmit?: (values: ICreateNameYourPageFormValues) => void;
  onChange?: (values: ICreateNameYourPageFormValues) => void;
  trackClickedPageNameIdeas?: () => void;
}

export interface ICreateNameYourPageFormValues {
  [PageNameStepInputNames.PageName]: string;
}

interface IRecipientFormValues extends ICreateNameYourPageFormValues, IRecipientInfoAddressFormValues {}

const blockName = 'name-your-page-step';
export const formId = 'CreateNameYourPageStepForm';

export default function NameYourPageStep({
  className,
  buttons,
  buttonsPortal,
  onChange,
  onSubmit,
  trackClickedPageNameIdeas,
  ...otherProps
}: INameYourPageStepProps) {
  const bem = bemBlock(blockName);
  const { t } = useTranslation();
  const { pageFormRef } = React.useContext(CreatePageContext);
  const formValues = useCreateFlowStore(state => state.formValues);
  const [values, setValues] = React.useState<IRecipientInfoAddressFormValues>();

  const openPageNameDrawer = useCreateFlowStore(state => state.openPageNameDrawer);

  const isMyself = formValues[AboutStepInputNames.RecipientType] === RecipientType.Myself;

  const schema = pageNameStepSchema().concat(recipientInfoAddressFormSchema(isMyself));

  function handleShowPageNameIdeas() {
    trackClickedPageNameIdeas?.();
    openPageNameDrawer();
  }

  function handleSubmit(values: IRecipientFormValues) {
    setValues(values);
    if (onSubmit) {
      onSubmit(values);
    }
  }

  function handleChange(values: IRecipientFormValues) {
    setValues(values);
    if (onChange) {
      onChange(values);
    }
  }

  function customValidateForm(values: IRecipientFormValues, opts: ValidationOptions) {
    // run the default validator
    const errors: ValidationErrors = validateForm(schema, values, opts);

    // remove some errors if the fields are hidden
    if (!values.includeRecipientAddress) {
      delete errors.address1;
      delete errors.address2;
      delete errors.city;
      delete errors.state;
      delete errors.postalCode;
    }

    return errors;
  }

  return (
    <PageSection className={bem(null, [{ myself: isMyself }], className)} noPad variant="aqua" gradientDarkening>
      <Form
        ref={pageFormRef}
        className={bem('form', null, ['gik-form--white'])}
        schema={schema}
        initialValues={formValues}
        vertical
        id={formId}
        onSubmit={handleSubmit}
        onChange={handleChange}
        validate={customValidateForm}
        trackingId={CreateAnalyticsEvents.CREATE_STARTED}
        mutators={{
          setPageName: (args, state, utils) => {
            utils.changeValue(state, PageNameStepInputNames.PageName, () => args[0]);
          },
        }}
        render={(form: FormRenderProps) => {
          return (
            <div>
              <PageSection className={bem('name-your-page-section')} noPadTop noPadBottom>
                <h2 className="gik-create-section-title">{t(translationKeys.PageNameTitle)}</h2>
                <p className={bem('page-name-description')}>
                  <HTMLParser rawHtml={t(translationKeys.PageNameDescription)} />
                </p>
                <FormField name={PageNameStepInputNames.PageName} center />

                <Button
                  className={bem('ideasBtn')}
                  pill
                  variant="primary-vdark"
                  size="sm"
                  prepend={<SvgIcon name="LightBulb" />}
                  onClick={() => handleShowPageNameIdeas()}
                >
                  {t(translationKeys.PageNameIdeasButton)}
                </Button>
              </PageSection>

              {isMyself && (
                <PageSection
                  className={bem('recipient-address')}
                  contained={false}
                  variant="solid-neutral"
                  noPadTop
                  noPadBottom
                >
                  <RecipientInfoAddressForm isMyself={isMyself} form={form} values={values} />
                </PageSection>
              )}

              <FormButtons buttons={buttons} buttonsPortal={buttonsPortal} form={form} formId={formId} />
            </div>
          );
        }}
        {...otherProps}
      />
    </PageSection>
  );
}
